/**
 * Open-Meteo Elevation API client — free, no key, fast.
 * https://open-meteo.com/en/docs/elevation-api
 *
 * Returns metres above sea level for a list of lat/lng pairs in one round-trip.
 * We use it to power the Course Flyover's "Plays Like" toggle: elevation delta
 * between the player's position and the green centre is folded back into the
 * displayed carry yardage so a 150-yard shot uphill plays like 158, etc.
 */

const ENDPOINT = "https://api.open-meteo.com/v1/elevation";
const cache = new Map(); // key = "lat,lng" rounded to 5 dp

function key(lat, lng) {
  return `${lat.toFixed(5)},${lng.toFixed(5)}`;
}

/** Fetch metres-above-sea-level for one or more lat/lng pairs.
 *  Returns a Map keyed by "lat.fixed5,lng.fixed5" → number (metres). */
export async function fetchElevations(points) {
  if (!Array.isArray(points) || points.length === 0) return new Map();
  // Use cache where we can, only fetch the misses.
  const out = new Map();
  const misses = [];
  points.forEach((p) => {
    const k = key(p.lat, p.lng);
    if (cache.has(k)) out.set(k, cache.get(k));
    else misses.push(p);
  });
  if (!misses.length) return out;

  const lats = misses.map((p) => p.lat.toFixed(5)).join(",");
  const lngs = misses.map((p) => p.lng.toFixed(5)).join(",");
  try {
    const res = await fetch(`${ENDPOINT}?latitude=${lats}&longitude=${lngs}`, {
      method: "GET",
    });
    if (!res.ok) throw new Error(`elevation ${res.status}`);
    const json = await res.json();
    const arr = Array.isArray(json.elevation) ? json.elevation : [];
    misses.forEach((p, i) => {
      const m = typeof arr[i] === "number" ? arr[i] : null;
      const k = key(p.lat, p.lng);
      if (m != null) {
        cache.set(k, m);
        out.set(k, m);
      }
    });
  } catch {
    /* swallow — UI falls back to flat-distance display */
  }
  return out;
}

export function elevationKey(lat, lng) { return key(lat, lng); }

/** Apply the classic "Plays Like" elevation adjustment to a carry distance.
 *  Each yard of elevation change adds (or removes) one yard of carry. The
 *  industry rule of thumb is roughly +/- 1 yd per foot of elevation, which is
 *  ~3.3 yds per metre. We use that here so the number is intuitive but not
 *  pretending to model wind / temperature / spin loft. */
export function playsLikeYards(flatYards, deltaMetres) {
  if (flatYards == null || deltaMetres == null) return flatYards;
  // Positive delta = target higher than player → ball plays longer.
  const adj = deltaMetres * 3.281; // 1 metre ≈ 3.281 ft ≈ ~1 yd of carry effect
  return Math.max(0, Math.round(flatYards + adj));
}
